import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Grid, Typography, Box, Button, TextField, MenuItem } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import "./GriStandardsSelector.css";
import SaveButton from "components/buttonsComponent/SaveButton";
import UserIdButton from "components/buttonsComponent/UserIdButton";
import ReportCountdownButton from "components/buttonsComponent/ReportCountdownButton";

const StandardsSelector = ({
  standards = [],
  ReportDownloadButtonShow = false,
  formImageUrl = "../../assets/images/GriCustomizeForm.png",
  downloadFileName = "Report_Downloaded.docx",
  buttonStyles = {},

  onNext,
  onSave,
  onBack,
  showNextButton = true,
  showBackButton = false,
  title = "Select from the options below and mark the appropriate choice",
  nextButtonText = "Next",
  backButtonText = "Back",
  showNavigationButtons = true,
  showSaveButton = true,
  onSelectionChange,
  initialSelectedStandards = [],
  className = "",
  showReportCountdownButton = false,
  initialActivityDetails = {},
  onActivityDetailsChange,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set(initialSelectedStandards || []));
  const [activityDetails, setActivityDetails] = useState(initialActivityDetails || {});
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  useEffect(() => {
    if (initialSelectedStandards && initialSelectedStandards.length > 0) {
      setSelectedIds(new Set(initialSelectedStandards));
    }
  }, [initialSelectedStandards]);

  useEffect(() => {
    if (initialActivityDetails && Object.keys(initialActivityDetails).length > 0) {
      setActivityDetails(initialActivityDetails);
    }
  }, [initialActivityDetails]);

  const handleDownloadClick = () => {
    const link = document.createElement("a");
    link.href = formImageUrl;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 2000);
  };

  const defaultButtonStyles = {
    download: {
      background: isDownloadEnabled
        ? "#02462E"
        : "#D3D3D3",
      width: "252px",
      height: "50px",
      color: "#fff",
      border: "none",
      textTransform: "none",
      fontFamily: "DM Sans",
      fontWeight: 600,
      fontSize: "13px",
      lineHeight: "1.2",
      cursor: isDownloadEnabled ? "pointer" : "not-allowed",
      padding: "8px 12px",
      borderRadius: "10px",
    },
    next: {
      background: "#02462E",
      width: "252px",
      height: "50px",
      borderRadius: "10px",
      color: "#fff",
      textTransform: "none",
      fontFamily: "DM Sans",
      fontWeight: 600,
      fontSize: "16px",
      "&:hover": {
        background: "#FEC700",
        color: "#02462E",
      },
    },
    back: {
      background: "#02462E",
      width: "252px",
      height: "50px",
      borderRadius: "10px",
      color: "#fff !important",
      textTransform: "none",
      fontFamily: "DM Sans",
      fontWeight: 600,
      fontSize: "16px",
      "&:hover": {
        background: "#FEC700",
        color: "#02462E !important",
      },
    },
  };

  const mergedButtonStyles = {
    download: { ...defaultButtonStyles.download, ...buttonStyles.download },
    next: { ...defaultButtonStyles.next, ...buttonStyles.next },
    back: { ...defaultButtonStyles.back, ...buttonStyles.back },
  };

  const processedStandards = useMemo(() => {
    return standards
      .map((group) => {
        if (group && group.isFormSection) {
          return null;
        }

        if (group && Array.isArray(group.items)) {
          return {
            heading: group.heading || null,
            items: group.items.map((standard) => ({
              ...standard,
              groupIndex: group.groupIndex,
              groupHeading: group.heading || null,
            })),
          };
        } else if (
          group &&
          typeof group === "object" &&
          !Array.isArray(group.items) &&
          !group.isFormSection
        ) {
          return {
            heading: null,
            items: [
              {
                ...group,
                groupHeading: null,
              },
            ],
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [standards]);

  const columns = useMemo(() => {
    const getColumns = (items, numberOfColumns = 2) => {
      const itemsPerColumn = Math.ceil(items.length / numberOfColumns);
      const columns = [];

      for (let i = 0; i < numberOfColumns; i++) {
        const start = i * itemsPerColumn;
        const end = start + itemsPerColumn;
        columns.push(items.slice(start, end));
      }

      return columns;
    };

    return getColumns(processedStandards);
  }, [processedStandards]);

  const getSelectedObjects = useCallback(() => {
    const selected = [];
    standards.forEach(group => {
      if (group.items) {
        group.items.forEach(standard => {
          const uniqueId = `${group.heading || "no-heading"}-${standard.id}`;
          if (selectedIds.has(uniqueId)) {
            selected.push({ ...standard, id: uniqueId, originalId: standard.id, heading: group.heading });
          }
        });
      }
    });
    return selected;
  }, [standards, selectedIds]);

  const handleCheckboxChange = useCallback((id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleActivityDetailChange = useCallback((fieldId, value) => {
    setActivityDetails((prev) => {
      return { ...prev, [fieldId]: value };
    });
  }, []);

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(getSelectedObjects());
    }
  }, [getSelectedObjects, onSelectionChange]);

  useEffect(() => {
    if (onActivityDetailsChange) {
      onActivityDetailsChange(activityDetails);
    }
  }, [activityDetails, onActivityDetailsChange]);

  const handleNext = useCallback(() => {
    if (onNext) {
      onNext(getSelectedObjects(), activityDetails);
    }
  }, [getSelectedObjects, activityDetails, onNext]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(getSelectedObjects(), activityDetails);
    }
  }, [getSelectedObjects, activityDetails, onSave]);

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack(getSelectedObjects(), activityDetails);
    }
  }, [getSelectedObjects, activityDetails, onBack]);

  const renderActivityDetailsField = (field) => {
    const value = activityDetails[field.id] || "";

    switch (field.inputType) {
      case "text":
        return (
          <TextField
            fullWidth
            variant="outlined"
            value={value}
            onChange={(e) => handleActivityDetailChange(field.id, e.target.value)}
            placeholder={field.placeholder || ""}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: "DM Sans",
              },
            }}
          />
        );

      case "dropdown":
        return (
          <TextField
            select
            fullWidth
            variant="outlined"
            value={value}
            onChange={(e) => handleActivityDetailChange(field.id, e.target.value)}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: "DM Sans",
              },
            }}
          >
            {field.options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );

      case "multiselect":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {field.options.map((option) => {
              const selectedValues = value ? value.split(",").map(v => v.trim()) : [];
              const isSelected = selectedValues.includes(option);

              return (
                <div className="checkbox-item" key={option}>
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        let newValues;
                        if (isSelected) {
                          newValues = selectedValues.filter(v => v !== option);
                        } else {
                          newValues = [...selectedValues, option];
                        }
                        handleActivityDetailChange(field.id, newValues.join(", "));
                      }}
                    />
                    <span className="checkmark"></span>
                    <span className="standard-label">{option}</span>
                  </label>
                </div>
              );
            })}
          </Box>
        );

      case "number":
        return (
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            value={value}
            onChange={(e) => handleActivityDetailChange(field.id, e.target.value)}
            placeholder={field.placeholder || ""}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: "DM Sans",
              },
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Grid container spacing={2} className={className}>
      <Grid item xs={12}>
        <Grid item xs={12}>
          <Box
            className="standards-header"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              [(theme) => theme.breakpoints.down("sm")]: {
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "stretch",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
              }}
            >
              {showReportCountdownButton && <ReportCountdownButton />}
              {showSaveButton && (
                <SaveButton>
                  Save
                </SaveButton>
              )}
              <UserIdButton />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid item xs={12} className="mx-2">
        <Typography variant="h6" className="selection-header">
          {title}
        </Typography>

        {standards.some(group => group?.isFormSection) && (
          <Box sx={{ mb: 4, mt: 2 }}>
            {standards
              .filter(group => group?.isFormSection)
              .map((section, sectionIndex) => (
                <Box key={sectionIndex} sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "DM Sans",
                      fontWeight: 600,
                      fontSize: "18px",
                      color: "#02462E",
                      mb: 3,
                    }}
                  >
                    {section.heading}
                  </Typography>
                  <Grid container spacing={2}>
                    {section.fields?.map((field) => (
                      <Grid item xs={12} md={6} key={field.id}>
                        <Typography
                          sx={{
                            fontFamily: "DM Sans",
                            fontWeight: 500,
                            fontSize: "14px",
                            color: "#02462E",
                            mb: 1,
                          }}
                        >
                          {field.label}
                        </Typography>
                        {renderActivityDetailsField(field)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
          </Box>
        )}

        <div className="standards-grid">
          {columns.map((column, columnIndex) => (
            <div key={columnIndex} className="standards-column">
              {column.map((group, groupIndex) => (
                <div
                  key={group.heading || `group-${columnIndex}-${groupIndex}`}
                  className="standards-group"
                >
                  {group.heading && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: "DM Sans",
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#02462E",
                        marginBottom: "8px",
                        marginTop: groupIndex > 0 ? "16px" : "0",
                      }}
                    >
                      {group.heading}
                    </Typography>
                  )}
                  {group.items.map((standard) => {
                    const uniqueId = `${group.heading || "no-heading"}-${standard.id}`;
                    const isChecked = selectedIds.has(uniqueId);
                    return (
                      <div className="checkbox-item" key={uniqueId}>
                        <label className="checkbox-container">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleCheckboxChange(uniqueId)}
                          />
                          <span className="checkmark"></span>
                          <span className="standard-label">{standard.label}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div
          className="button-container"
          style={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}
        >
          {ReportDownloadButtonShow && (
            <Button
              variant="outlined"
              disabled={!isDownloadEnabled}
              onClick={handleDownloadClick}
              style={mergedButtonStyles.download}
            >
              Download Reports
            </Button>
          )}

          {showBackButton && (
            <Button
              variant="contained"
              className="back-button"
              onClick={handleBack}
              sx={mergedButtonStyles.back}
            >
              {backButtonText}
            </Button>
          )}

          {showNextButton && (
            <Button
              variant="contained"
              className="next-button"
              onClick={handleNext}
              sx={mergedButtonStyles.next}
            >
              {nextButtonText}
            </Button>
          )}
        </div>

      </Grid>
    </Grid>
  );
};

export default StandardsSelector;